/**
 * V4.42 — Bouton "Copier briefing stratégique".
 * V4.42.1 — Compatibilité iOS Safari + modal de secours.
 *
 * Doctrine : export quotidien déterministe (aucune IA) prêt à coller dans ChatGPT.
 * Le rituel cible :
 *   1. Cliquer "Copier briefing stratégique"
 *   2. Coller dans ChatGPT
 *   3. Recevoir le plan d'action du jour
 *   4. Exécuter
 *
 * Variantes :
 *   - variant="primary" (default) : bouton plein, large, pour Pilotage
 *   - variant="ghost" : bouton compact pour Commando (à côté de l'objectif du jour)
 *
 * Compatibilité iOS Safari (V4.42.1) :
 *   - iOS Safari exige que `navigator.clipboard.writeText` soit appelé DANS le
 *     contexte synchrone du user gesture. Notre fetch async casse ce contrat.
 *   - Stratégie 1 : `navigator.clipboard.write([new ClipboardItem({ "text/plain": Promise<Blob> })])`
 *     est appelé synchronement dans le clic — la Promise est résolue après
 *     le fetch, mais le browser garde le user gesture (supporté iOS ≥ 13.4).
 *   - Stratégie 2 (fallback Chrome/Firefox) : fetch puis `writeText`.
 *   - Stratégie 3 (ultime) : ouvrir une modal plein écran avec textarea +
 *     boutons "Sélectionner tout" / "Copier" — copie manuelle garantie.
 *   - Le toast "Briefing copié" n'apparaît que sur succès confirmé.
 */
import { useState } from "react";
import { toast } from "sonner";
import { Clipboard, CheckCircle, Spinner } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { track, USAGE_EVENTS } from "@/lib/usage";
import BriefingFallbackModal from "./BriefingFallbackModal";

const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  // iOS Safari + iPadOS Safari (qui se déclare Mac depuis iPadOS 13)
  const ua = navigator.userAgent || "";
  const isIPad = /iPad/i.test(ua) || (ua.includes("Mac") && "ontouchend" in document);
  return /iPhone|iPod/i.test(ua) || isIPad;
};

async function fetchBriefing() {
  const { data } = await api.get("/briefing/strategique");
  return {
    markdown: data?.markdown || "",
    sizeKb:
      data?.size_kb ??
      Math.round(((data?.markdown?.length || 0) / 1024) * 100) / 100,
  };
}

/**
 * Tentative iOS-safe : ClipboardItem avec une Promise<Blob>.
 * Le user gesture est préservé même si la Promise se résout après un await.
 * Retourne { ok: boolean, markdown, sizeKb }.
 */
async function copyViaClipboardItem() {
  if (typeof window === "undefined" || !navigator.clipboard?.write || !window.ClipboardItem) {
    return { ok: false, reason: "ClipboardItem unsupported" };
  }

  // On capture la Promise du fetch dans le ClipboardItem AVANT toute attente
  let captured = { markdown: "", sizeKb: 0 };
  const textPromise = fetchBriefing()
    .then((res) => {
      captured = res;
      return new Blob([res.markdown], { type: "text/plain" });
    })
    .catch((err) => {
      console.error("[Briefing] fetch inside ClipboardItem failed:", err);
      throw err;
    });

  try {
    const item = new window.ClipboardItem({ "text/plain": textPromise });
    await navigator.clipboard.write([item]);
    console.info("[Briefing] clipboard.write(ClipboardItem) OK", captured.sizeKb, "KB");
    return { ok: true, markdown: captured.markdown, sizeKb: captured.sizeKb };
  } catch (err) {
    // Récupère le markdown si fetch a réussi malgré l'échec clipboard
    let md = captured.markdown;
    let sz = captured.sizeKb;
    if (!md) {
      try {
        const res = await textPromise.then((blob) => blob.text());
        md = res;
        sz = Math.round((res.length / 1024) * 100) / 100;
      } catch {
        /* fetch failed elsewhere */
      }
    }
    console.warn("[Briefing] clipboard.write(ClipboardItem) failed:", err?.name, err?.message);
    return { ok: false, reason: err?.message || "clipboard.write failed", markdown: md, sizeKb: sz };
  }
}

/**
 * Tentative classique : fetch puis writeText. Ne fonctionne PAS sur iOS Safari
 * après un await (perte de user gesture).
 */
async function copyViaWriteText() {
  let captured;
  try {
    captured = await fetchBriefing();
  } catch (err) {
    console.error("[Briefing] fetch failed:", err);
    return { ok: false, reason: "fetch failed" };
  }
  if (!navigator.clipboard?.writeText) {
    return { ok: false, reason: "clipboard.writeText unsupported", ...captured };
  }
  try {
    await navigator.clipboard.writeText(captured.markdown);
    console.info("[Briefing] clipboard.writeText OK", captured.sizeKb, "KB");
    return { ok: true, ...captured };
  } catch (err) {
    console.warn("[Briefing] clipboard.writeText failed:", err?.name, err?.message);
    return { ok: false, reason: err?.message || "writeText failed", ...captured };
  }
}

export default function BriefingStrategiqueButton({ variant = "primary", testid = "briefing-copy-btn" }) {
  const [state, setState] = useState("idle"); // idle | loading | done
  const [fallback, setFallback] = useState(null); // { markdown, sizeKb } | null

  const handleCopy = async (e) => {
    e?.stopPropagation();
    if (state === "loading") return;
    setState("loading");

    // Stratégie 1 : ClipboardItem avec Promise — iOS-safe.
    // Appelé SYNCHRONEMENT dans le user gesture (pas d'await avant).
    let result = await copyViaClipboardItem();

    // Stratégie 2 : writeText classique (Chrome/Firefox desktop).
    // Skip sur iOS car le user gesture est déjà perdu après l'await ci-dessus.
    if (!result.ok && !isIOS()) {
      result = await copyViaWriteText();
    }

    if (result.ok) {
      track(USAGE_EVENTS.BRIEFING_COPIED, {
        meta: { size_kb: result.sizeKb, variant, method: "clipboard" },
      });
      toast.success(`Briefing copié — ${result.sizeKb} KB`, {
        description: "Colle-le dans ChatGPT pour ton plan d'action du jour.",
      });
      setState("done");
      window.setTimeout(() => setState("idle"), 2200);
      return;
    }

    // Stratégie 3 : modal de secours. Toast d'erreur uniquement si markdown indispo.
    if (result.markdown) {
      console.warn("[Briefing] Falling back to manual copy modal:", result.reason);
      setFallback({ markdown: result.markdown, sizeKb: result.sizeKb });
      setState("idle");
    } else {
      toast.error("Erreur lors de la génération du briefing");
      console.error("[Briefing] Failure (no markdown available):", result.reason);
      setState("idle");
    }
  };

  const closeFallback = () => {
    setFallback(null);
    // Le tracking est posé ici même si la copie a réussi via la modal,
    // car la modal a déjà émis son propre toast en cas de succès.
    if (fallback) {
      track(USAGE_EVENTS.BRIEFING_COPIED, {
        meta: { size_kb: fallback.sizeKb, variant, method: "modal" },
      });
    }
  };

  const isLoading = state === "loading";
  const isDone = state === "done";
  const Icon = isDone ? CheckCircle : isLoading ? Spinner : Clipboard;

  return (
    <>
      {variant === "ghost" ? (
        <button
          type="button"
          onClick={handleCopy}
          disabled={isLoading}
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[12px] font-extrabold transition-colors ${
            isDone
              ? "bg-emerald-50 text-emerald-700"
              : "bg-white/10 text-white hover:bg-white/20"
          } ${isLoading ? "opacity-70 cursor-wait" : ""}`}
          title="Copier le briefing stratégique pour ChatGPT"
          data-testid={testid}
        >
          <Icon size={14} weight={isLoading ? "bold" : "fill"} className={isLoading ? "animate-spin" : ""} />
          <span>{isDone ? "Copié" : isLoading ? "Copie…" : "Briefing ChatGPT"}</span>
        </button>
      ) : (
        <button
          type="button"
          onClick={handleCopy}
          disabled={isLoading}
          className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-xl text-[13px] font-extrabold transition-all border ${
            isDone
              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
              : "bg-slate-900 text-white border-slate-900 hover:bg-slate-800 hover:border-slate-800 active:scale-[0.98]"
          } ${isLoading ? "opacity-70 cursor-wait" : ""}`}
          title="Génère un briefing complet (objectifs, pipeline, apporteurs, prospection) prêt à coller dans ChatGPT"
          data-testid={testid}
        >
          <Icon size={16} weight={isLoading ? "bold" : "fill"} className={isLoading ? "animate-spin" : ""} />
          <span>
            {isDone ? "Briefing copié ✓" : isLoading ? "Génération en cours…" : "Copier briefing stratégique"}
          </span>
        </button>
      )}

      {fallback && (
        <BriefingFallbackModal
          markdown={fallback.markdown}
          sizeKb={fallback.sizeKb}
          onClose={closeFallback}
        />
      )}
    </>
  );
}
